import {
  type TableV2HostDependencies,
  type TableV2RenderSnapshot,
} from '@databases/frontend/table-v2-host';
import { type Recyclable } from '../bootstrap/types.ts';
import { type TableV2ViewSession } from './create-table-v2-view-session.ts';

const unavailableSnapshot: TableV2RenderSnapshot = { status: 'unavailable' };

/**
 * Connects V2 view sessions to the existing bootstrap coordinator. The coordinator
 * disposes before setup on database/view/layout changes; the supplied factory
 * returns undefined for other layouts, so they acquire no V2 session.
 *
 * The host handle stays stable while its render snapshot is replaced. This lets
 * TableV2Host follow view switches without remounting the application root. Its
 * notifications describe session replacement, not cell edits. Permission-only
 * selection rebuilds read the same session without recapturing data.
 */
export function createTableV2ViewLifecycle(
  createSessionForView: (contentId: string) => TableV2ViewSession | undefined,
) {
  let currentSession: TableV2ViewSession | undefined;
  let renderSnapshot = unavailableSnapshot;
  const hostSubscribers = new Set<VoidFunction>();

  const notifyHost = () => {
    for (const listener of hostSubscribers) {
      listener();
    }
  };

  const host: TableV2HostDependencies = {
    getSnapshot: () => renderSnapshot,
    subscribe(listener) {
      hostSubscribers.add(listener);

      return () => {
        hostSubscribers.delete(listener);
      };
    },
  };

  const lifecycle: Recyclable = {
    recycleOn: ['view-id-changed', 'layout-type-changed'],
    setup(contentId) {
      currentSession = createSessionForView(contentId);
      renderSnapshot = currentSession?.renderSnapshot ?? unavailableSnapshot;

      notifyHost();
    },
    dispose() {
      const previousSession = currentSession;
      currentSession = undefined;
      renderSnapshot = unavailableSnapshot;

      // Readers must observe unavailability even if the old session's cleanup throws.
      try {
        previousSession?.dispose();
      } finally {
        notifyHost();
      }
    },
  };

  return {
    lifecycle,
    host,
    getSelectionDependencies: () => currentSession?.selectionDependencies,
  };
}
